export const CartState = {
  getCart() {
    return JSON.parse(localStorage.getItem('quoteBuilderCart') || '[]');
  },

  saveCart(cart) {
    localStorage.setItem('quoteBuilderCart', JSON.stringify(cart));
    document.dispatchEvent(new CustomEvent('quoteCartUpdated', { detail: { cart } }));
  },

  addItem(item) {
    const cart = this.getCart();
    
    // We match by id AND parentId to ensure identical items with different parents are distinct
    const existingIndex = cart.findIndex(i => i.id === item.id && (i.parentId || '') === (item.parentId || ''));
    const qty = item.quantity || 1;
    
    if (existingIndex > -1) {
      cart[existingIndex].quantity += qty;
    } else {
      cart.push({ ...item, quantity: qty });
    }
    this.saveCart(cart);
  },

  updateQuantity(id, quantity, parentId = '') {
    const cart = this.getCart();
    const existingIndex = cart.findIndex(i => i.id === id && (i.parentId || '') === parentId);
    if (existingIndex > -1) {
      if (quantity > 0) {
        cart[existingIndex].quantity = quantity;
      } else {
        // Splice handles standard removal
        cart.splice(existingIndex, 1);
      }
      this.saveCart(cart);
    }
  },

  removeItem(id, parentId = '') {
    let cart = this.getCart();
    
    // Remove the specific item itself
    cart = cart.filter(i => !(i.id === id && (i.parentId || '') === parentId));
    
    // If the removed item was a parent (meaning it had NO parentId), ALSO remove ALL its accessories
    if (!parentId) {
      cart = cart.filter(i => i.parentId !== id);
    }
    
    this.saveCart(cart);
  },

  clearCart() {
    this.saveCart([]);
  }
};

function showToast(message) {
  const toast = document.createElement('div');
  toast.className = 'quote-toast';
  toast.textContent = message;
  document.body.appendChild(toast);

  // Trigger animation
  setTimeout(() => toast.classList.add('active'), 10);

  // Remove after 3 seconds
  setTimeout(() => {
    toast.classList.remove('active');
    setTimeout(() => toast.remove(), 400);
  }, 3000);
}

export function quoteBuilderInit() {
  const renderCart = () => {
    const cart = CartState.getCart();
    const quoteItemsLists = document.querySelectorAll('.quote-items-list');

    quoteItemsLists.forEach(list => {
      list.innerHTML = ''; // Clear current contents
      
      // Step 1: Render Top-Level Parents (items with NO parentId)
      const topLevelItems = cart.filter(item => !item.parentId);
      
      topLevelItems.forEach(parentItem => {
        // Render Parent HTML
        const parentHtml = renderQuoteItemHTML(parentItem, false);
        list.insertAdjacentHTML('beforeend', parentHtml);
        
        // Step 2: Render its children (if any)
        const children = cart.filter(child => (child.parentId || '') === parentItem.id);
        children.forEach(childItem => {
          const childHtml = renderQuoteItemHTML(childItem, true);
          list.insertAdjacentHTML('beforeend', childHtml);
        });
      });
      
      // Edge-case Handle items that have a parentId, but the parent itself is missing
      const orphanedChildren = cart.filter(child => child.parentId && !topLevelItems.find(p => p.id === child.parentId));
      orphanedChildren.forEach(childItem => {
         const childHtml = renderQuoteItemHTML(childItem, true); // Still render as accessory
         list.insertAdjacentHTML('beforeend', childHtml);
      });
    });
  };

  const renderQuoteItemHTML = (item, isAccessory) => {
    return `
      <div class="quote-item ${isAccessory ? 'accessory-item' : ''}" data-id="${item.id}" data-parent-id="${item.parentId || ''}">
        <div class="item-image-container">
          ${item.permalink ? `<a href="${item.permalink}">` : ''}
          <img src="${item.image || 'https://placehold.co/80x80'}" alt="${item.name}" class="item-image">
          ${item.permalink ? `</a>` : ''}
          ${isAccessory ? `
            <svg class="item-move-up accessory-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <rect width="24" height="24" rx="12" fill="white" />
              <path
                d="M15.64 11.5153C15.6052 11.5502 15.5638 11.5779 15.5183 11.5967C15.4728 11.6156 15.424 11.6253 15.3747 11.6253C15.3254 11.6253 15.2766 11.6156 15.2311 11.5967C15.1856 11.5779 15.1442 11.5502 15.1094 11.5153L12.3747 8.78018V16.125C12.3747 16.2245 12.3352 16.3199 12.2649 16.3902C12.1946 16.4605 12.0992 16.5 11.9997 16.5C11.9003 16.5 11.8049 16.4605 11.7346 16.3902C11.6642 16.3199 11.6247 16.2245 11.6247 16.125V8.78018L8.89003 11.5153C8.81967 11.5857 8.72423 11.6252 8.62472 11.6252C8.52521 11.6252 8.42977 11.5857 8.35941 11.5153C8.28904 11.445 8.24951 11.3495 8.24951 11.25C8.24951 11.1505 8.28904 11.0551 8.35941 10.9847L11.7344 7.60971C11.7692 7.57484 11.8106 7.54718 11.8561 7.52831C11.9016 7.50944 11.9504 7.49973 11.9997 7.49973C12.049 7.49973 12.0978 7.50944 12.1433 7.52831C12.1888 7.54718 12.2302 7.57484 12.265 7.60971L15.64 10.9847C15.6749 11.0195 15.7026 11.0609 15.7214 11.1064C15.7403 11.1519 15.75 11.2007 15.75 11.25C15.75 11.2993 15.7403 11.3481 15.7214 11.3936C15.7026 11.4391 15.6749 11.4805 15.64 11.5153Z"
                fill="#2C2C2C" />
            </svg>
          ` : ''}
        </div>
        <div class="item-details">
          ${item.permalink ? `<a href="${item.permalink}">` : ''}
          <div class="item-name subheading-12">${item.name}</div>
          ${item.permalink ? `</a>` : ''}
          <div class="item-specs subheading-10">${item.specs}</div>
          <div class="quantaty-and-remove">
            <div class="item-quantity-control">
              <button class="qty-minus" type="button">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M10.5 6C10.5 6.09946 10.4605 6.19484 10.3902 6.26517C10.3198 6.33549 10.2245 6.375 10.125 6.375H1.875C1.77554 6.375 1.68016 6.33549 1.60984 6.26517C1.53951 6.19484 1.5 6.09946 1.5 6C1.5 5.90054 1.53951 5.80516 1.60984 5.73484C1.68016 5.66451 1.77554 5.625 1.875 5.625H10.125C10.2245 5.625 10.3198 5.66451 10.3902 5.73484C10.4605 5.80516 10.5 5.90054 10.5 6Z" fill="#2C2C2C" />
                </svg>
              </button>
              <span class="qty-number label">${item.quantity}</span>
              <button class="qty-plus" type="button">
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M9.5 5C9.5 5.09946 9.46049 5.19484 9.39017 5.26517C9.31984 5.33549 9.22446 5.375 9.125 5.375H5.375V9.125C5.375 9.22446 5.33549 9.31984 5.26517 9.39017C5.19484 9.46049 5.09946 9.5 5 9.5C4.90054 9.5 4.80516 9.46049 4.73484 9.39017C4.66451 9.31984 4.625 9.22446 4.625 9.125V5.375H0.875C0.775544 5.375 0.680161 5.33549 0.609835 5.26517C0.539509 5.19484 0.5 5.09946 0.5 5C0.5 4.90054 0.539509 4.80516 0.609835 4.73484C0.680161 4.66451 0.775544 4.625 0.875 4.625H4.625V0.875C4.625 0.775544 4.66451 0.680161 4.73484 0.609835C4.80516 0.539509 4.90054 0.5 5 0.5C5.09946 0.5 5.19484 0.539509 5.26517 0.609835C5.33549 0.680161 5.375 0.775544 5.375 0.875V4.625H9.125C9.22446 4.625 9.31984 4.66451 9.39017 4.73484C9.46049 4.80516 9.5 4.90054 9.5 5Z" fill="#2C2C2C" />
                </svg>
              </button>
            </div>
            <div class="remove-item" role="button" tabindex="0">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M10.125 2.25H1.875C1.77554 2.25 1.68016 2.28951 1.60984 2.35984C1.53951 2.43016 1.5 2.52554 1.5 2.625C1.5 2.72446 1.53951 2.81984 1.60984 2.89016C1.68016 2.96049 1.77554 3 1.875 3H2.25V9.75C2.25 9.94891 2.32902 10.1397 2.46967 10.2803C2.61032 10.421 2.80109 10.5 3 10.5H9C9.19891 10.5 9.38968 10.421 9.53033 10.2803C9.67098 10.1397 9.75 9.94891 9.75 9.75V3H10.125C10.2245 3 10.3198 2.96049 10.3902 2.89016C10.4605 2.81984 10.5 2.72446 10.5 2.625C10.5 2.52554 10.4605 2.43016 10.3902 2.35984C10.3198 2.28951 10.2245 2.25 10.125 2.25ZM9 9.75H3V3H9V9.75ZM3.75 1.125C3.75 1.02554 3.78951 0.930161 3.85984 0.859835C3.93016 0.789509 4.02554 0.75 4.125 0.75H7.875C7.97446 0.75 8.06984 0.789509 8.14017 0.859835C8.21049 0.930161 8.25 1.02554 8.25 1.125C8.25 1.22446 8.21049 1.31984 8.14017 1.39017C8.06984 1.46049 7.97446 1.5 7.875 1.5H4.125C4.02554 1.5 3.93016 1.46049 3.85984 1.39017C3.78951 1.31984 3.75 1.22446 3.75 1.125Z" fill="#2C2C2C"/>
              </svg>
            </div>
            </div>
        </div>
       </div>
    `;
  };

   const updateItemCounters = () => {
     const cart = CartState.getCart();
     const count = cart.reduce((acc, item) => acc + item.quantity, 0);
     
     // Update all relevant counters text
     const countNumberElements = document.querySelectorAll('.builder-header .count-number, .car-icon .number-label');
     countNumberElements.forEach(el => {
       el.textContent = count;
     });
     
     // Hide or show counters depending on the count
     const itemCountContainers = document.querySelectorAll('.builder-header .item-count');
     const numberLabels = document.querySelectorAll('.car-icon .number-label');
     const clearAllButtons = document.querySelectorAll('.clear-all');
     const enquireButtons = document.querySelectorAll('.enquire-btn');
     
     if (count === 0) {
       itemCountContainers.forEach(el => el.style.display = 'none');
       numberLabels.forEach(el => el.style.display = 'none');
       clearAllButtons.forEach(el => el.style.display = 'none');
       enquireButtons.forEach(el => el.style.display = 'none');
     } else {
       itemCountContainers.forEach(el => el.style.display = '');
       numberLabels.forEach(el => el.style.display = '');
       clearAllButtons.forEach(el => el.style.display = '');
       enquireButtons.forEach(el => el.style.display = '');
     }
   };

   // Listen to state changes
   document.addEventListener('quoteCartUpdated', () => {
     renderCart();
     updateItemCounters();
   });

   // 1. Initial Render
   renderCart();
   updateItemCounters();

   // 2. Global Event Delegation
   document.body.addEventListener('click', (e) => {
     
     // --- Add To Quote ---
     const addToQuoteBtn = e.target.closest('.add-to-quote-btn');
     if (addToQuoteBtn) {
       e.preventDefault(); // In case it's inside a link
       const id = addToQuoteBtn.getAttribute('data-id');
       const name = addToQuoteBtn.getAttribute('data-name');
       const specs = addToQuoteBtn.getAttribute('data-specs');
       const image = addToQuoteBtn.getAttribute('data-image') || 'https://placehold.co/80x80';
       const permalink = addToQuoteBtn.getAttribute('data-permalink') || '';
       const productType = addToQuoteBtn.getAttribute('data-product-type') || '';
       let parentId = '';

       // If product_type === "attachments" -> treat it as an accessory
       // Otherwise -> treat it as a main (parent) product
       if (productType === "attachments") {
         // Identify parentId based on the current context (e.g. main product on the page)
         const mainProductContainer = document.querySelector('.single-product-page[data-main-product-id]');
         if (mainProductContainer) {
           parentId = mainProductContainer.getAttribute('data-main-product-id');
         }
       }
       
       // Only add if ID exists
       if (id) {
        // Try to find a quantity selector in the same container
        let quantity = 1;
        const container = addToQuoteBtn.closest('.add-to-quote, .product_details_block, .quote-box-wrapper, .quote-product-card');
        if (container) {
          const qtyEl = container.querySelector('.quantity-number, .count-number');
          if (qtyEl) {
            quantity = parseInt(qtyEl.textContent, 10) || 1;
          }
        }
         CartState.addItem({ id, name, specs, image, permalink, parentId, quantity });
         showToast('Product added');
        
        // Open the popup automatically if needed
        const quotesPanel = document.getElementById('menu-quote');
        if (quotesPanel) {
          // Open logic here depending on the menu toggle
        }
      }
      return;
    }
 
     // --- Quantity Plus ---
     const plusBtn = e.target.closest('.qty-plus');
     if (plusBtn) {
       const quoteItem = plusBtn.closest('.quote-item');
       if (quoteItem) {
         const id = quoteItem.getAttribute('data-id');
         const parentId = quoteItem.getAttribute('data-parent-id') || '';
         const cart = CartState.getCart();
         const item = cart.find(i => i.id === id && (i.parentId || '') === parentId);
         if (item) {
           CartState.updateQuantity(id, item.quantity + 1, parentId);
         }
       }
       return;
     }
 
     // --- Quantity Minus ---
     const minusBtn = e.target.closest('.qty-minus');
     if (minusBtn) {
       const quoteItem = minusBtn.closest('.quote-item');
       if (quoteItem) {
         const id = quoteItem.getAttribute('data-id');
         const parentId = quoteItem.getAttribute('data-parent-id') || '';
         const cart = CartState.getCart();
         const item = cart.find(i => i.id === id && (i.parentId || '') === parentId);
         if (item && item.quantity > 1) {
           CartState.updateQuantity(id, item.quantity - 1, parentId);
         }
       }
       return;
     }

     // --- Product Card Quantity Plus ---
     const cardPlusBtn = e.target.closest('.plus');
     if (cardPlusBtn && !cardPlusBtn.closest('.quote-item')) {
       const container = cardPlusBtn.closest('.counter');
       if (container) {
         const qtyEl = container.querySelector('.count-number');
         if (qtyEl) {
           qtyEl.textContent = (parseInt(qtyEl.textContent, 10) || 0) + 1;
         }
       }
       return;
     }

     // --- Product Card Quantity Minus ---
     const cardMinusBtn = e.target.closest('.minus');
     if (cardMinusBtn && !cardMinusBtn.closest('.quote-item')) {
       const container = cardMinusBtn.closest('.counter');
       if (container) {
         const qtyEl = container.querySelector('.count-number');
         if (qtyEl) {
           const current = parseInt(qtyEl.textContent, 10) || 1;
           if (current > 1) {
             qtyEl.textContent = current - 1;
           }
         }
       }
       return;
     }
 
     // --- Remove Item ---
     const removeBtn = e.target.closest('.remove-item');
     if (removeBtn) {
       const quoteItem = removeBtn.closest('.quote-item');
       if (quoteItem) {
         const id = quoteItem.getAttribute('data-id');
         const parentId = quoteItem.getAttribute('data-parent-id') || '';
         CartState.removeItem(id, parentId);
       }
       return;
    }
 
     // --- Clear All ---
    const clearAllBtn = e.target.closest('.clear-all');
     if (clearAllBtn) {
       CartState.clearCart();
       return;
     }
   });
 }
